import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { format } from "date-fns";

import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";

import { SettingsCard } from "./SettingsCard";
import { LeaveWorkspaceDialog } from "./LeaveWorkspaceDialog";
import { RejoinWorkspaceDialog } from "./RejoinWorkspaceDialog";

import { Button } from "@/components/ui/button";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";

export const StudentUserStatusCard = () => {
  const { t, i18n } = useTranslation(["workspaces"]);
  const { settings } = useWorkspaceSettingsStore();
  const { data: workspaceInfo } = useWorkspaceInformationStore();
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [isRejoinDialogOpen, setIsRejoinDialogOpen] = useState(false);

  if (!settings) return null;

  const { user_status } = settings;

  const getStatusInfo = () => {
    const knowledgeCut = workspaceInfo?.knowledge_cut;
    const formattedKnowledgeCut = knowledgeCut
      ? format(new Date(knowledgeCut), "PPP", {
          locale: getDateFnsLocale(i18n.language),
        })
      : null;

    if (user_status.is_blocked_for_user) {
      return {
        icon: "mdi:block-helper",
        title: t("workspaces:settings.studentStatus.blocked", "Your Status: Blocked"),
        variant: "destructive" as const,
        description: t("workspaces:settings.studentStatus.blockedDescription", {
          defaultValue:
            "You have limited access to this workspace, you can only see records up to {{knowledgeCut}}",
          knowledgeCut: formattedKnowledgeCut,
        }),
        topCircle: "from-red-600 to-pink-500",
        bottomCircle: "from-red-500 to-orange-500",
        showLeaveButton: false,
        showRejoinButton: false,
      };
    }

    if (user_status.has_user_left) {
      return {
        icon: "mdi:exit-to-app",
        title: t("workspaces:settings.studentStatus.left", "Your Status: Inactive"),
        variant: "secondary" as const,
        description: t("workspaces:settings.studentStatus.leftDescription", {
          defaultValue:
            "You have limited access to this workspace, you can only see records up to {{knowledgeCut}}",
          knowledgeCut: formattedKnowledgeCut,
        }),
        topCircle: "from-gray-600 to-gray-500",
        bottomCircle: "from-gray-500 to-gray-400",
        showLeaveButton: false,
        showRejoinButton: true,
      };
    }

    if (!user_status.is_active_for_user) {
      return {
        icon: "mdi:pause-circle",
        title: t("workspaces:settings.studentStatus.inactive", "Your Status: Inactive"),
        variant: "secondary" as const,
        description: t("workspaces:settings.studentStatus.inactiveDescription", {
          defaultValue:
            "You have limited access to this workspace, you can only see records up to {{knowledgeCut}}",
          knowledgeCut: formattedKnowledgeCut,
        }),
        topCircle: "from-yellow-600 to-orange-500",
        bottomCircle: "from-yellow-500 to-red-500",
        showLeaveButton: false,
        showRejoinButton: false,
      };
    }

    return {
      icon: "mdi:account-school",
      title: t("workspaces:settings.studentStatus.active", "Your Status: Active"),
      variant: "default" as const,
      description: t("workspaces:settings.studentStatus.activeDescription", {
        defaultValue: "You have full access to this workspace",
      }),
      topCircle: "from-blue-600 to-indigo-500",
      bottomCircle: "from-blue-500 to-purple-500",
      showLeaveButton: true,
      showRejoinButton: false,
    };
  };

  const statusInfo = getStatusInfo();

  const action = (
    <div className="flex items-center gap-2">
      {statusInfo.showLeaveButton && (
        <Button
          className="h-6 gap-1 px-2 text-xs"
          size="sm"
          variant="destructive"
          onClick={() => setIsLeaveDialogOpen(true)}
        >
          <Icon icon="mdi:exit-to-app" />
          {t("workspaces:leaveWorkspace.leave")}
        </Button>
      )}
      {statusInfo.showRejoinButton && (
        <Button
          className="h-6 gap-1 px-2 text-xs"
          size="sm"
          variant="foreground"
          onClick={() => setIsRejoinDialogOpen(true)}
        >
          <Icon icon="mdi:account-plus" />
          {t("rejoinWorkspace.rejoin", { defaultValue: "Rejoin" })}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <SettingsCard
        action={action}
        bottomCircle={statusInfo.bottomCircle}
        bottomCirclePlacement="bottom-right"
        bottomCircleSize="h-16 w-16"
        description={statusInfo.description}
        icon={statusInfo.icon}
        title={statusInfo.title}
        topCircle={statusInfo.topCircle}
        topCirclePlacement="top-left"
        topCircleSize="h-20 w-20"
      />
      <LeaveWorkspaceDialog
        isOpen={isLeaveDialogOpen}
        onClose={() => setIsLeaveDialogOpen(false)}
      />
      <RejoinWorkspaceDialog
        isOpen={isRejoinDialogOpen}
        onClose={() => setIsRejoinDialogOpen(false)}
      />
    </>
  );
};
